// frontend/src/askos/mentoring/BookMentoringPage.js
//
// Student browses every mentoring-enabled faculty member (admin-gated,
// not project-scoped — any student can book any of them), each shown
// with their assigned project name(s) for orientation, then views and
// books that faculty's open slots.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Button, Spinner, Alert, ListGroup, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { listMentoringFaculty, listOpenMentoringSlots, bookMentoringSlot } from '../../api/mentoringApi';

const BookMentoringPage = ({ slug }) => {
    const [facultyList, setFacultyList] = useState(null);
    const [selectedFaculty, setSelectedFaculty] = useState(null);
    const [slots, setSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        listMentoringFaculty(slug).then(setFacultyList).catch(() => setFacultyList([]));
    }, [slug]);

    const openFaculty = useCallback((faculty) => {
        setSelectedFaculty(faculty);
        setLoadingSlots(true);
        setError(''); setSuccess('');
        listOpenMentoringSlots(slug, faculty.faculty_user_id)
            .then(setSlots)
            .catch(() => setSlots([]))
            .finally(() => setLoadingSlots(false));
    }, [slug]);

    const handleBook = async (slotId) => {
        setError(''); setSuccess('');
        try {
            await bookMentoringSlot(slug, slotId);
            setSuccess('Slot booked! Find it under "My Bookings".');
            setSlots(prev => prev.filter(s => s.id !== slotId));
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not book this slot — it may have just been taken.');
        }
    };

    if (!facultyList) {
        return <div className="d-flex justify-content-center p-5"><Spinner animation="border" /></div>;
    }

    return (
        <div className="book-mentoring-page p-3">
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h4 className="fw-bold mb-0">Book a 1:1 Mentoring Session</h4>
                    <p className="text-muted small mb-0">Sessions are up to 30 minutes, video call only — no translation.</p>
                </div>
                <Link to="/samkalp-ias/mentoring/my-bookings" className="btn btn-sm btn-outline-secondary">My Bookings</Link>
            </div>

            {!selectedFaculty ? (
                <>
                    {facultyList.length === 0 && <p className="text-muted">No faculty are currently open for mentoring bookings.</p>}
                    <div className="d-flex flex-wrap gap-3">
                        {facultyList.map(f => (
                            <Card key={f.faculty_user_id} style={{ width: 260, cursor: 'pointer' }} onClick={() => openFaculty(f)}>
                                <Card.Body>
                                    <Card.Title className="fs-6">{f.name}</Card.Title>
                                    <Card.Text className="small text-muted">
                                        {f.project_names.length ? f.project_names.join(', ') : 'No assigned subjects'}
                                    </Card.Text>
                                    <Button size="sm" variant="outline-primary">View Slots</Button>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <Button size="sm" variant="link" className="mb-2 ps-0" onClick={() => setSelectedFaculty(null)}>&larr; Back to faculty list</Button>
                    <h5>{selectedFaculty.name}</h5>
                    {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                    {success && <Alert variant="success" className="py-2 small">{success}</Alert>}
                    {loadingSlots ? (
                        <Spinner animation="border" size="sm" />
                    ) : slots.length === 0 ? (
                        <p className="text-muted">No open slots right now — check back later.</p>
                    ) : (
                        <ListGroup>
                            {slots.map(s => (
                                <ListGroup.Item key={s.id} className="d-flex justify-content-between align-items-center">
                                    <span>{new Date(s.start_utc).toLocaleString()} <Badge bg="light" text="dark" className="border ms-2">30 min</Badge></span>
                                    <Button size="sm" onClick={() => handleBook(s.id)}>Book</Button>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </>
            )}
        </div>
    );
};

export default BookMentoringPage;
